import React, { useMemo, useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';
import { EditorView } from '@codemirror/view';
import {
  Terminal, ChevronRight, Plus, Variable, X, Braces, Clock, ArrowRight,
  Play, Loader2, CheckCircle2, XCircle, RotateCcw, Copy, Check, Sparkles, AlertTriangle,
} from 'lucide-react';
import { workflowService, EntityFieldInfo, RunPythonTestResult } from '../../services/WorkflowService';

// A component that picks a context variable (or a free-text value). Passed in
// from WorkflowsPage so this editor stays decoupled from that module.
type VariablePickerComponent = React.ComponentType<{
  value: string | null;
  contextVars: EntityFieldInfo[];
  placeholder?: string;
  onChange: (v: string | null) => void;
  allowFreeText?: boolean;
  allowNull?: boolean;
}>;

interface RunPythonNodeEditorProps {
  config: Record<string, any>;
  nodeId: string;
  contextVars: EntityFieldInfo[];
  cfg: (key: string, value: any) => void;
  VariablePicker: VariablePickerComponent;
}

const CM_THEME = EditorView.theme({
  '&': { background: '#fff', color: '#1e1e1e' },
  '.cm-gutters': { background: '#f9fafb', borderRight: '1px solid #e5e7eb', color: '#9ca3af' },
  '.cm-activeLineGutter': { background: '#f3f4f6' },
  '.cm-activeLine': { background: '#f3f4f6' },
  '.cm-cursor': { borderLeftColor: '#6d28d9' },
  '.cm-selectionBackground, ::selection': { background: '#ddd6fe !important' },
});

/** Build a starter sample-inputs JSON from the node's input_mapping keys, so the
 *  author sees exactly which keys `inputs[...]` will hold. */
function sampleFromMapping(mapping: Record<string, string | null>): string {
  const keys = Object.keys(mapping ?? {});
  if (!keys.length) return '{\n  \n}';
  return '{\n' + keys.map(k => `  ${JSON.stringify(k)}: null`).join(',\n') + '\n}';
}

/**
 * Editor panel for a `run_python` node — extracted out of WorkflowsPage so the
 * (large) snippet UI lives on its own.
 *
 * Layout: a two-column workbench on wide screens — the code editor on the left,
 * the test-run console (sample inputs → Run → output/logs) pinned on the right —
 * so authors can edit and re-run without scrolling. ⌘/Ctrl+Enter runs from
 * anywhere in the panel. The test run executes the snippet server-side in the
 * same sandbox the workflow uses, so a passing debug run means a passing node.
 */
const RunPythonNodeEditor: React.FC<RunPythonNodeEditorProps> = ({
  config, nodeId, contextVars, cfg, VariablePicker,
}) => {
  const mapping: Record<string, string | null> = config.input_mapping ?? {};
  const mappingEntries = Object.entries(mapping);

  // Sample inputs persist on the node (config._test_inputs) so they survive
  // reopening the modal; seeded from the mapping keys the first time.
  const [sampleText, setSampleText] = useState<string>(
    () => config._test_inputs ?? sampleFromMapping(mapping),
  );
  const [running, setRunning] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [result, setResult] = useState<RunPythonTestResult | null>(null);
  const [copiedPath, setCopiedPath] = useState(false);

  // AI code generation — the prompt persists on the node so iterating is easy.
  const [genPrompt, setGenPrompt] = useState<string>(() => config._codegen_prompt ?? '');
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [genInfo, setGenInfo] = useState<{ explanation: string; syntaxWarning?: string | null } | null>(null);

  const outputPath = useMemo(() => `node_results.${nodeId}.output`, [nodeId]);

  const setSample = (v: string) => { setSampleText(v); cfg('_test_inputs', v); };

  const run = async () => {
    if (running) return;
    let inputs: Record<string, any> = {};
    const txt = sampleText.trim();
    if (txt) {
      try {
        inputs = JSON.parse(txt);
      } catch (e) {
        setJsonError(`Invalid JSON — ${(e as Error).message}`);
        return;
      }
      if (typeof inputs !== 'object' || inputs === null || Array.isArray(inputs)) {
        setJsonError('Sample inputs must be a JSON object, e.g. { "price": 12.5 }.');
        return;
      }
    }
    setJsonError(null);
    setRunning(true);
    setResult(null);
    try {
      const r = await workflowService.testRunPython({
        code: config.code ?? '',
        inputs,
        timeout_seconds: config.timeout_seconds ?? 10,
      });
      setResult(r.data);
    } catch (e: any) {
      setResult({ success: false, error: e?.response?.data?.detail || e?.message || 'Request failed' });
    } finally {
      setRunning(false);
    }
  };

  const generate = async () => {
    const prompt = genPrompt.trim();
    if (!prompt || generating) return;
    setGenerating(true);
    setGenError(null);
    setGenInfo(null);
    try {
      const r = await workflowService.generateRunPython({
        prompt,
        current_code: (config.code ?? '').trim() || undefined,
        input_keys: Object.keys(mapping),
        context_vars: contextVars.map(v => v.path),
      });
      cfg('code', r.data.code);
      setGenInfo({
        explanation: r.data.explanation,
        syntaxWarning: r.data.syntax_ok ? null : (r.data.syntax_error || 'generated code may not be valid — review before running'),
      });
    } catch (e: any) {
      setGenError(e?.response?.data?.detail || e?.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // ⌘/Ctrl+Enter: generates when pressed inside the AI-prompt box, runs the
  // test everywhere else (captured before CodeMirror inserts a newline).
  const onKeyDownCapture = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if ((e.target as HTMLElement)?.closest?.('[data-codegen-prompt]')) generate();
      else run();
    }
  };

  const copyPath = async () => {
    try {
      await navigator.clipboard.writeText(outputPath);
      setCopiedPath(true);
      setTimeout(() => setCopiedPath(false), 1500);
    } catch { /* clipboard unavailable — ignore */ }
  };

  return (
    <div className="space-y-3" onKeyDownCapture={onKeyDownCapture}>
      {/* ── Header banner ──────────────────────────────────────────────── */}
      <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-indigo-50 px-4 py-2.5 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center flex-shrink-0 shadow-sm">
          <Terminal className="w-4 h-4 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-violet-900 flex items-center gap-1.5">
            Python Lambda Node
            <span className="px-1.5 py-0.5 rounded-full bg-violet-200 text-violet-700 text-[9px] font-semibold tracking-wide uppercase">sandbox</span>
          </p>
          <p className="text-[10px] text-violet-600 mt-0.5 leading-relaxed">
            Define <code className="font-mono bg-violet-100/80 px-1 py-0.5 rounded text-violet-700">async def execute(inputs, context, extras)</code> and <code className="font-mono bg-violet-100/80 px-1 py-0.5 rounded text-violet-700">return</code> a JSON-serialisable value.
            HTTP via <code className="font-mono bg-violet-100/80 px-1 py-0.5 rounded text-violet-700">httpx</code> is allowed.
          </p>
        </div>
        <span className="hidden lg:inline-flex items-center gap-1 text-[10px] text-violet-400 font-mono flex-shrink-0" title="Run the test from anywhere in this panel">
          ⌘↵ <span className="text-violet-300">run</span>
        </span>
      </div>

      {/* ── Workbench: code (left) · test run (right) ──────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-3 items-start">
        {/* ── Left column — inputs + code ─────────────────────────────── */}
        <div className="xl:col-span-3 space-y-3 min-w-0">
          {/* Input mapping */}
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-1.5">
                <ChevronRight className="w-3 h-3 text-gray-400" />
                <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide">
                  Inputs <span className="normal-case font-normal text-gray-400">→ available as <code className="font-mono text-violet-600">inputs["key"]</code></span>
                </span>
              </div>
              <button type="button"
                onClick={() => cfg('input_mapping', { ...mapping, [`var${mappingEntries.length + 1}`]: '' })}
                className="flex items-center gap-1 text-[10px] text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-lg px-2 py-1 transition-colors font-medium">
                <Plus className="w-3 h-3" /> Add input
              </button>
            </div>
            {mappingEntries.length === 0 ? (
              <div className="px-4 py-3 text-center">
                <p className="text-[10px] text-gray-400 flex items-center justify-center gap-1.5">
                  <Variable className="w-3.5 h-3.5 text-gray-300" />
                  No inputs yet — click <strong>Add input</strong> to map a context variable.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50/50 text-[9px] font-semibold text-gray-400 uppercase tracking-wider">
                  <span className="w-28 flex-shrink-0">Variable name</span>
                  <span className="flex-1">Maps to</span>
                </div>
                {mappingEntries.map(([k, v], idx) => (
                  <div key={idx} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50/80 transition-colors">
                    <div className="w-28 flex-shrink-0">
                      <input
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-[11px] font-mono bg-white focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 transition-all"
                        value={k}
                        placeholder="name"
                        onChange={e => cfg('input_mapping', Object.fromEntries(mappingEntries.map(([mk, mv], i) => [i === idx ? e.target.value : mk, mv])))}
                      />
                    </div>
                    <div className="flex-1">
                      <VariablePicker value={v} contextVars={contextVars} placeholder="context path or static value"
                        onChange={newV => cfg('input_mapping', Object.fromEntries(mappingEntries.map(([mk, mv], i) => [mk, i === idx ? newV : mv])))} />
                    </div>
                    <button type="button"
                      onClick={() => cfg('input_mapping', Object.fromEntries(mappingEntries.filter((_, i) => i !== idx)))}
                      className="p-1.5 text-gray-300 hover:text-red-400 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* AI code generation */}
          <div className="rounded-xl border border-fuchsia-200 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-fuchsia-50/70 border-b border-fuchsia-200">
              <div className="flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-fuchsia-600" />
                <span className="text-[10px] font-semibold text-fuchsia-800 uppercase tracking-wide">Generate with AI</span>
                <span className="normal-case font-normal text-fuchsia-400 text-[10px] hidden sm:inline">— describe what the step should do; existing code is modified, not lost context</span>
              </div>
              <button type="button"
                onClick={generate}
                disabled={generating || !genPrompt.trim()}
                title="⌘/Ctrl + Enter inside the prompt"
                className="flex items-center gap-1.5 text-[10px] font-semibold text-white bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg px-3 py-1 transition-colors flex-shrink-0">
                {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                {generating ? 'Generating…' : 'Generate'}
              </button>
            </div>
            <div className="px-3 py-2.5 space-y-1.5" data-codegen-prompt>
              <textarea
                value={genPrompt}
                onChange={e => { setGenPrompt(e.target.value); cfg('_codegen_prompt', e.target.value); }}
                spellCheck={false}
                rows={2}
                disabled={generating}
                className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-xs leading-relaxed bg-white focus:outline-none focus:ring-2 focus:ring-fuchsia-300 focus:border-fuchsia-300 transition-all resize-y disabled:bg-gray-50 disabled:text-gray-400"
                placeholder='e.g. "Take the supplier rows from inputs, pick the cheapest in-stock one, and return its price, currency and supplier name"'
              />
              <p className="text-[9px] text-gray-400">
                The generator knows the sandbox rules (execute() signature, allowed modules, JSON output),
                your mapped input keys{Object.keys(mapping).length ? ` (${Object.keys(mapping).join(', ')})` : ''} and this workflow's context variables.
                If code already exists it is <strong>modified</strong> to fit the request.
              </p>
              {genError && <p className="text-[10px] text-red-500">{genError}</p>}
              {genInfo && (
                <div className="rounded-lg border border-fuchsia-100 bg-fuchsia-50/50 px-2.5 py-2 space-y-1">
                  <p className="text-[10px] text-fuchsia-700 leading-relaxed">{genInfo.explanation}</p>
                  {genInfo.syntaxWarning && (
                    <p className="text-[10px] text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 flex-shrink-0" /> {genInfo.syntaxWarning}
                    </p>
                  )}
                  <p className="text-[9px] text-fuchsia-400">Code inserted below — press Run to test it before saving.</p>
                </div>
              )}
            </div>
          </div>

          {/* Code editor */}
          <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white border border-gray-200">
                <Braces className="w-3 h-3 text-violet-500" />
                <span className="text-[10px] font-mono text-gray-500">snippet.py</span>
              </div>
              <span className="text-[9px] text-gray-400 font-mono">Python 3 · sandbox</span>
            </div>
            <CodeMirror
              value={config.code ?? ''}
              height="calc(100vh - 420px)"
              minHeight="380px"
              extensions={[python(), CM_THEME]}
              basicSetup={{
                lineNumbers: true,
                foldGutter: true,
                highlightActiveLine: true,
                highlightActiveLineGutter: true,
                autocompletion: true,
                indentOnInput: true,
                bracketMatching: true,
                closeBrackets: true,
              }}
              style={{ fontSize: '13px' }}
              onChange={v => cfg('code', v)}
            />
            <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-t border-gray-200 text-[9px] font-mono text-gray-400">
              <span>{(config.code ?? '').split('\n').length} lines · {(config.code ?? '').length} chars</span>
              <span>UTF-8 · Python</span>
            </div>
          </div>

          {/* Available names quick-reference */}
          <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5 space-y-2">
            <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">Available in snippet</p>
            <div className="flex flex-wrap gap-1.5">
              {[
                { name: 'inputs', color: 'text-violet-700 bg-violet-50 border-violet-200', tip: 'Your mapped input variables (dict)' },
                { name: 'context', color: 'text-indigo-700 bg-indigo-50 border-indigo-200', tip: 'entity, event_payload, node_results, foreach_item' },
                { name: 'extras', color: 'text-pink-700 bg-pink-50 border-pink-200', tip: 'extras["cache"] — org/workflow-scoped Redis cache; extras["logger"] — logger' },
                { name: 'return', color: 'text-emerald-700 bg-emerald-50 border-emerald-200', tip: 'Return a JSON-serialisable value to pass to the next node' },
                { name: 'httpx', color: 'text-cyan-700 bg-cyan-50 border-cyan-200', tip: 'Async HTTP — async with httpx.AsyncClient() as client' },
                { name: 'asyncio', color: 'text-teal-700 bg-teal-50 border-teal-200', tip: 'asyncio utilities' },
                { name: 'json', color: 'text-gray-700 bg-gray-100 border-gray-200', tip: 'json.loads / json.dumps' },
                { name: 'datetime', color: 'text-gray-700 bg-gray-100 border-gray-200', tip: 'datetime.datetime, timedelta, …' },
                { name: 're', color: 'text-gray-700 bg-gray-100 border-gray-200', tip: 'Regular expressions' },
                { name: 'math', color: 'text-gray-700 bg-gray-100 border-gray-200', tip: 'math.floor, math.ceil, …' },
              ].map(({ name, color, tip }) => (
                <span key={name} title={tip}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-mono cursor-help ${color}`}>
                  {name}
                </span>
              ))}
              <span className="text-[10px] text-gray-400 self-center">+ itertools · functools · decimal · collections</span>
            </div>
          </div>
        </div>

        {/* ── Right column — test-run console + settings ──────────────── */}
        <div className="xl:col-span-2 space-y-3 min-w-0 xl:sticky xl:top-0">
          {/* Test run */}
          <div className="rounded-xl border border-violet-200 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-violet-50/70 border-b border-violet-200">
              <div className="flex items-center gap-1.5 min-w-0">
                <Play className="w-3 h-3 text-violet-600 flex-shrink-0" />
                <span className="text-[10px] font-semibold text-violet-800 uppercase tracking-wide">Test run</span>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button type="button"
                  onClick={() => setSample(sampleFromMapping(mapping))}
                  title="Reset sample inputs from the mapping keys"
                  className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-violet-700 hover:bg-violet-50 border border-gray-200 rounded-lg px-2 py-1 transition-colors">
                  <RotateCcw className="w-3 h-3" /> Reset
                </button>
                <button type="button"
                  onClick={run}
                  disabled={running}
                  title="⌘/Ctrl + Enter"
                  className="flex items-center gap-1.5 text-[10px] font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg px-3 py-1 transition-colors">
                  {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                  {running ? 'Running…' : 'Run'}
                </button>
              </div>
            </div>

            {/* Sample inputs */}
            <div className="px-3 py-2.5 space-y-1.5">
              <label className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide">
                Sample <code className="font-mono text-violet-600 normal-case">inputs</code> (JSON)
              </label>
              <textarea
                value={sampleText}
                onChange={e => setSample(e.target.value)}
                spellCheck={false}
                rows={Math.min(12, Math.max(4, sampleText.split('\n').length))}
                className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-xs font-mono leading-relaxed bg-gray-50 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 focus:bg-white transition-all resize-y"
                placeholder='{ "price": 12.5 }'
              />
              {jsonError && <p className="text-[10px] text-red-500 font-mono">{jsonError}</p>}
              <p className="text-[9px] text-gray-400">Sandbox only — no workflow run, nothing persisted.</p>
            </div>

            {/* Result */}
            {!result && !running && (
              <div className="border-t border-gray-100 px-3 py-4 bg-gray-50/60 text-center">
                <p className="text-[10px] text-gray-400">
                  Output appears here. Press <strong>Run</strong> or <kbd className="font-mono bg-white border border-gray-200 rounded px-1 py-0.5 text-[9px]">⌘↵</kbd>.
                </p>
              </div>
            )}
            {running && (
              <div className="border-t border-gray-100 px-3 py-4 bg-gray-50/60 flex items-center justify-center gap-2 text-violet-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span className="text-[10px] font-medium">Running in the sandbox…</span>
              </div>
            )}
            {result && (
              <div className="border-t border-gray-100 px-3 py-2.5 space-y-2 bg-gray-50/60">
                <div className="flex items-center gap-1.5">
                  {result.success
                    ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    : <XCircle className="w-3.5 h-3.5 text-red-500" />}
                  <span className={`text-[10px] font-semibold ${result.success ? 'text-emerald-700' : 'text-red-700'}`}>
                    {result.success ? 'Success' : 'Failed'}
                  </span>
                  {typeof result.duration_ms === 'number' && (
                    <span className="text-[9px] font-mono text-gray-400">· {result.duration_ms} ms</span>
                  )}
                </div>

                {!result.success && result.error && (
                  <pre className="text-[11px] font-mono text-red-600 bg-red-50 border border-red-100 rounded-lg px-2.5 py-2 whitespace-pre-wrap break-words max-h-64 overflow-auto">{result.error}</pre>
                )}

                {result.success && (
                  <div className="space-y-1">
                    <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                      <ArrowRight className="w-3 h-3" /> Output
                    </p>
                    <pre className="text-[11px] font-mono text-gray-800 bg-white border border-gray-200 rounded-lg px-2.5 py-2 whitespace-pre-wrap break-words max-h-96 overflow-auto">
                      {(() => { try { return JSON.stringify(result.output, null, 2); } catch { return String(result.output); } })()}
                    </pre>
                  </div>
                )}

                <div className="space-y-1">
                  <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide">
                    Sandbox logs <span className="normal-case font-normal text-gray-400">— print() + logger</span>
                  </p>
                  <pre className="text-[11px] font-mono text-gray-600 bg-gray-900/[0.03] border border-gray-200 rounded-lg px-2.5 py-2 whitespace-pre-wrap break-words max-h-64 overflow-auto leading-relaxed">
                    {result.logs || <span className="text-gray-400 italic">— no log output (use print(…) or logger.info(…) to see logs here) —</span>}
                  </pre>
                </div>
              </div>
            )}
          </div>

          {/* Timeout */}
          <div className="rounded-xl border border-gray-200 px-3 py-2.5 space-y-1.5">
            <label className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-600 uppercase tracking-wide">
              <Clock className="w-3 h-3" /> Timeout
            </label>
            <div className="flex items-center gap-2">
              <input type="range" min={1} max={600} step={5}
                className="flex-1 accent-violet-600"
                value={config.timeout_seconds ?? 10}
                onChange={e => cfg('timeout_seconds', Number(e.target.value))}
              />
              <span className="text-xs font-mono font-semibold text-gray-700 w-12 text-right">
                {config.timeout_seconds ?? 10} s
              </span>
            </div>
            <p className="text-[9px] text-gray-400">Max 600 s. Step fails if exceeded.</p>
          </div>

          {/* Output path */}
          <div className="rounded-xl border border-violet-100 bg-violet-50/60 px-3 py-2.5 space-y-1.5">
            <p className="text-[10px] font-semibold text-violet-700 uppercase tracking-wide flex items-center gap-1">
              <ArrowRight className="w-3 h-3" /> Output path
            </p>
            <button type="button" onClick={copyPath} title="Copy to clipboard"
              className="w-full flex items-center justify-between gap-2 font-mono text-[10px] text-violet-800 bg-violet-100/80 hover:bg-violet-100 rounded-lg px-2 py-1.5 break-all leading-relaxed text-left transition-colors">
              <span className="min-w-0 break-all">{outputPath}</span>
              {copiedPath ? <Check className="w-3 h-3 text-emerald-600 flex-shrink-0" /> : <Copy className="w-3 h-3 text-violet-400 flex-shrink-0" />}
            </button>
            <p className="text-[9px] text-violet-500">Click to copy — use this path in downstream nodes.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RunPythonNodeEditor;
